/**
 * AgentsPage — the cloud LIS RELAY-AGENT cockpit (NOVA-070). An on-prem agent dials the lab's analyzers
 * (T14 at the box) and relays each result to this cloud, where the SAME ingest runs. This screen stewards
 * the relay fleet: register an agent (which mints a ONE-TIME pairing code shown once), watch the live
 * online/offline badge (heartbeat-driven, polled), and revoke a compromised agent (the kill-switch).
 *
 * Gated by lis.agent.manage (lab_admin / manager + owner). The pairing code is the only secret the UI ever
 * sees — displayed once in a modal, never re-fetched (the server stores only its hash). Status is always
 * color + glyph + text (daltonisme + médico-légal).
 */
import { useState } from "react";
import {
  Badge,
  Button,
  Card,
  EmptyState,
  FormField,
  Input,
  Modal,
  Table,
  useToast,
  type BadgeTone,
  type Column,
} from "@/ui";
import { useT } from "@/app";
import { useCan } from "@/auth";
import { token } from "@/design/theme";
import { CAP, useAgents, useCreateAgent, useRevokeAgent } from "./api";
import type { CreateAgentResult, LisAgent, LisAgentStatut } from "./types";
import { PageHeader } from "./components/PageHeader";
import { QueryBoundary } from "./components/QueryBoundary";
import { fmtDateTime, idStr } from "./lib";

const STATUT_TONE: Record<LisAgentStatut, BadgeTone> = {
  en_attente: "info",
  actif: "success",
  revoque: "neutral",
};

/** The live presence badge — online (heartbeat fresh + actif) vs offline. Color + glyph + text. */
function presenceBadge(a: LisAgent, t: (k: string) => string) {
  if (a.statut !== "actif") return null;
  return a.online ? (
    <Badge tone="success">● {t("lis.agents.online")}</Badge>
  ) : (
    <Badge tone="danger">○ {t("lis.agents.offline")}</Badge>
  );
}

export function AgentsPage() {
  const { t } = useT();
  const canManage = useCan(CAP.agentManage);
  const agentsQuery = useAgents();
  const [showAdd, setShowAdd] = useState(false);
  const [pairing, setPairing] = useState<CreateAgentResult | null>(null);

  return (
    <div>
      <PageHeader
        title={t("lis.agents.title")}
        subtitle={t("lis.agents.subtitle")}
        actions={
          canManage ? (
            <Button variant="primary" onClick={() => setShowAdd(true)}>
              {t("lis.agents.add")}
            </Button>
          ) : undefined
        }
      />

      <QueryBoundary q={agentsQuery}>
        {(agents) =>
          agents.length === 0 ? (
            <EmptyState
              glyph="◫"
              title={t("lis.agents.empty.title")}
              description={t("lis.agents.empty.desc")}
              {...(canManage
                ? {
                    action: (
                      <Button variant="primary" onClick={() => setShowAdd(true)}>
                        {t("lis.agents.add")}
                      </Button>
                    ),
                  }
                : {})}
            />
          ) : (
            <AgentsTable agents={agents} canManage={canManage} />
          )
        }
      </QueryBoundary>

      {showAdd && (
        <CreateAgentModal
          onClose={() => setShowAdd(false)}
          onCreated={(res) => {
            setShowAdd(false);
            setPairing(res); // immediately surface the one-time code
          }}
        />
      )}

      {pairing && <PairingCodeModal result={pairing} onClose={() => setPairing(null)} />}
    </div>
  );
}

function AgentsTable({ agents, canManage }: { agents: LisAgent[]; canManage: boolean }) {
  const { t } = useT();
  const [toRevoke, setToRevoke] = useState<LisAgent | null>(null);

  const columns: ReadonlyArray<Column<LisAgent>> = [
    {
      key: "nom",
      header: t("lis.agents.col.nom"),
      cell: (a) => (
        <span style={{ display: "flex", alignItems: "center", gap: token.s2, flexWrap: "wrap" }}>
          <strong>{a.nom}</strong>
          {presenceBadge(a, t)}
        </span>
      ),
    },
    {
      key: "statut",
      header: t("lis.agents.col.statut"),
      cell: (a) => <Badge tone={STATUT_TONE[a.statut]}>{t(`lis.agents.statut.${a.statut}`)}</Badge>,
    },
    {
      key: "presence",
      header: t("lis.agents.col.presence"),
      mono: true,
      cell: (a) => fmtDateTime(a.derniere_presence_at),
    },
    {
      key: "version",
      header: t("lis.agents.col.version"),
      mono: true,
      cell: (a) => a.version_agent ?? "—",
    },
    {
      key: "ip",
      header: t("lis.agents.col.ip"),
      mono: true,
      cell: (a) => a.adresse_ip_vue ?? "—",
    },
    {
      key: "actions",
      header: "",
      cell: (a) =>
        canManage && a.statut !== "revoque" ? (
          <Button variant="danger" size="sm" onClick={() => setToRevoke(a)}>
            {t("lis.agents.revoke")}
          </Button>
        ) : null,
    },
  ];

  return (
    <>
      <Card padded={false}>
        <Table<LisAgent>
          columns={columns}
          rows={agents}
          rowKey={(a) => idStr(a.id)}
          caption={t("lis.agents.title")}
        />
      </Card>
      {toRevoke && <RevokeModal agent={toRevoke} onClose={() => setToRevoke(null)} />}
    </>
  );
}

function CreateAgentModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (res: CreateAgentResult) => void;
}) {
  const { t } = useT();
  const toast = useToast();
  const create = useCreateAgent();
  const [nom, setNom] = useState("");
  const valid = nom.trim().length > 0;

  const submit = () => {
    if (!valid) return;
    create.mutate(
      { nom: nom.trim() },
      {
        onSuccess: (res) => {
          toast.success(t("lis.agents.created"));
          onCreated(res);
        },
        onError: (err) => toast.error(err.message, t("lis.agents.createError")),
      },
    );
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={t("lis.agents.addTitle")}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("lis.action.cancel")}
          </Button>
          <Button variant="primary" onClick={submit} loading={create.isPending} disabled={!valid}>
            {t("lis.agents.add")}
          </Button>
        </>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <FormField label={t("lis.agents.field.nom")} required>
          {(p) => (
            <Input
              {...p}
              value={nom}
              onChange={(e) => setNom(e.target.value)}
              autoFocus
              placeholder="Paillasse centrale — poste 1"
            />
          )}
        </FormField>
        <button type="submit" hidden aria-hidden tabIndex={-1} />
      </form>
    </Modal>
  );
}

/** The one-time pairing-code modal — shown ONCE; copy then forget (the server stores only its hash). */
function PairingCodeModal({ result, onClose }: { result: CreateAgentResult; onClose: () => void }) {
  const { t } = useT();
  const toast = useToast();
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(result.pairing_code);
      toast.success(t("lis.agents.pairingCopied"));
    } catch {
      /* clipboard unavailable — the code is visible to copy manually */
    }
  };
  return (
    <Modal
      open
      onClose={onClose}
      title={t("lis.agents.pairingTitle")}
      footer={
        <Button variant="primary" onClick={onClose}>
          {t("lis.agents.done")}
        </Button>
      }
    >
      <div style={{ display: "flex", flexDirection: "column", gap: token.s3 }}>
        <p style={{ margin: 0, display: "flex", gap: token.s2, alignItems: "flex-start" }}>
          <span aria-hidden style={{ color: token.warn }}>
            ▲
          </span>
          {t("lis.agents.pairingNote")}
        </p>
        <div
          className="nv-mono nv-ltr-island"
          style={{
            padding: token.s3,
            borderRadius: token.rMd,
            background: token.azureSoft,
            wordBreak: "break-all",
            userSelect: "all",
            fontSize: token.fsBody,
          }}
        >
          {result.pairing_code}
        </div>
        <div style={{ display: "flex", gap: token.s2, alignItems: "center" }}>
          <Button variant="secondary" size="sm" onClick={copy}>
            {t("lis.agents.pairingCopy")}
          </Button>
          <span style={{ color: token.slate, fontSize: token.fsLabel }}>
            {t("lis.agents.pairingExpires", { at: fmtDateTime(result.pairing_expires_at) })}
          </span>
        </div>

        {/* Install instructions — how the on-prem agent consumes this one-time code. */}
        <div style={{ borderTop: `1px solid ${token.line}`, paddingTop: token.s3 }}>
          <p style={{ margin: `0 0 ${token.s2}`, fontWeight: 600, color: token.ink }}>
            {t("lis.agents.installTitle")}
          </p>
          <ol style={{ margin: 0, paddingInlineStart: token.s4, color: token.slate, display: "flex", flexDirection: "column", gap: token.s2 }}>
            <li>{t("lis.agents.installStep1")}</li>
            <li>{t("lis.agents.installStep2")}</li>
            <li>{t("lis.agents.installStep3")}</li>
          </ol>
        </div>
      </div>
    </Modal>
  );
}

function RevokeModal({ agent, onClose }: { agent: LisAgent; onClose: () => void }) {
  const { t } = useT();
  const toast = useToast();
  const revoke = useRevokeAgent();
  const submit = () => {
    revoke.mutate(agent.id, {
      onSuccess: () => {
        toast.success(t("lis.agents.revoked"));
        onClose();
      },
      onError: (err) => toast.error(err.message, t("lis.agents.revokeError")),
    });
  };
  return (
    <Modal
      open
      onClose={onClose}
      title={t("lis.agents.revokeTitle")}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("lis.action.cancel")}
          </Button>
          <Button variant="danger" onClick={submit} loading={revoke.isPending}>
            {t("lis.agents.revoke")}
          </Button>
        </>
      }
    >
      <p style={{ margin: 0, display: "flex", gap: token.s2, alignItems: "flex-start" }}>
        <span aria-hidden style={{ color: token.danger }}>
          ▲
        </span>
        {t("lis.agents.revokeWarn")}
      </p>
    </Modal>
  );
}
